// Payment Types

export type PaymentServiceType = 'WEBS' | 'WEBSR' | 'RCG' | 'RCP' | 'EVT' | 'DNT' | 'ECM' | 'FAT' | 'ESTB' | 'WBS' | 'EST' | 'PSHOP';

export interface PaymentService {
  ID: string;
  NOME: string;
  DESCRICAO?: string;
  LOGO?: string;
  MIME_TYPE?: string;
  CODIGO?: string; // Service type code (RCG, WBS, EST, etc.)
  ID_PAYMENT_PAGE?: string; // Payment page ID for SISP
  ENTIDADE_ID?: string;
  CATEGORIA_ID?: string;
  ATIVO?: boolean;
}

export interface PaymentFormData {
  idTemp: string;
  tipoServico: PaymentServiceType;
  montante: string;
  referencia: string;
  entidade?: string;
  idCardWallet?: string;
}

export interface PaymentRequest {
  serviceId: string;
  amount: number;
  reference: string;
  entityCode?: string;
  walletCardId?: string;
  serviceType: PaymentServiceType;
}

export interface PaymentResponse {
  success: boolean;
  paymentUrl?: string;
  transactionId?: string;
  error?: string;
}

// SISP Payment States
export const SISP_PAYMENT_STATES = {
  N: { label: 'Pago', color: '#2BC155' },
  S: { label: 'Submetido', color: '#2196F3' },
  F: { label: 'Falhou', color: '#FF3E3E' },
  E: { label: 'Estornado', color: '#FF9800' },
  P: { label: 'Pendente', color: '#9E9E9E' },
} as const;

// Service type configurations
export const SERVICE_TYPE_CONFIG: Record<PaymentServiceType, {
  label: string;
  referenceLabel: string;
  referenceHint: string;
  showEntityField: boolean;
}> = {
  RCG: {
    label: 'Recarga',
    referenceLabel: 'Número',
    referenceHint: 'Insira o número de telemóvel',
    showEntityField: false,
  },
  WBS: {
    label: 'Serviço Web',
    referenceLabel: 'Referência',
    referenceHint: 'Insira a Referência de pagamento (9 dígitos)',
    showEntityField: false,
  },
  EST: {
    label: 'Serviço Estado',
    referenceLabel: 'Referência',
    referenceHint: 'Insira a Referência de pagamento',
    showEntityField: true,
  },
  EVT: {
    label: 'Evento',
    referenceLabel: 'Referência',
    referenceHint: 'Selecione os bilhetes',
    showEntityField: false,
  },
  DNT: {
    label: 'Donativo',
    referenceLabel: 'Referência',
    referenceHint: 'Insira o valor do donativo',
    showEntityField: false,
  },
  PSHOP: {
    label: 'Loja',
    referenceLabel: 'Referência',
    referenceHint: 'Pagamento de produtos',
    showEntityField: false,
  },
  WEBS: {
    label: 'Serviço Web',
    referenceLabel: 'Referência',
    referenceHint: 'Referência de pagamento',
    showEntityField: false,
  },
  WEBSR: {
    label: 'Serviço Web (Ref)',
    referenceLabel: 'Referência',
    referenceHint: 'Referência de pagamento',
    showEntityField: false,
  },
  RCP: {
    label: 'Recarga Pagali',
    referenceLabel: 'Número',
    referenceHint: 'Número do cartão Pagali',
    showEntityField: false,
  },
  ECM: {
    label: 'E-Commerce',
    referenceLabel: 'Referência',
    referenceHint: 'Referência da encomenda',
    showEntityField: false,
  },
  FAT: {
    label: 'Fatura',
    referenceLabel: 'Referência',
    referenceHint: 'Referência da fatura',
    showEntityField: false,
  },
  ESTB: {
    label: 'Saldo Municipal',
    referenceLabel: 'Referência',
    referenceHint: 'Referência do saldo',
    showEntityField: true,
  },
};

// Predefined package options for certain services
export interface PackageOption {
  value: string;
  label: string;
}

export interface ServicePackageConfig {
  idTemp: string;
  title: string;
  options: PackageOption[];
}

export const SERVICE_PACKAGE_OPTIONS: ServicePackageConfig[] = [
  {
    idTemp: '416776F6-EBF9-4D23-54B0-C5D8DDB7EFE3', // ALOU-Recarga Megas (SISP 105) — BT202308E
    title: 'Escolhe seus Megas',
    options: [
      { value: '300', label: '300$ | 1.000MB' },
      { value: '500', label: '500$ | 2.000MB' },
      { value: '1500', label: '1.500$ | 6.000MB' },
      { value: '3000', label: '3.000$ | 25.000MB' },
      { value: '5000', label: '5.000$ | 62.500MB' },
    ],
  },
  {
    idTemp: '6C879F71-B674-8A7D-85F1-64A35D48BADF', // Unitel Play (SISP 108)
    title: 'Escolhe Pacote Play',
    options: [
      { value: '150', label: 'Play Score 150$' },
      { value: '300', label: 'Play + Score 300$' },
      { value: '500', label: 'Play High Score 500$' },
      { value: '700', label: 'Play High Score I 700$' },
      { value: '700', label: 'Play + High Score I 700$' },
      { value: '1000', label: 'Play High Score II 1000$' },
      { value: '1000', label: 'Play + High Score II 1000$' },
    ],
  },
  {
    idTemp: '3D959333-3F4C-04B4-3D81-E0188C643DDC', // Unitel Internet (SISP 109)
    title: 'Escolhe Pacote Internet',
    options: [
      { value: '490', label: 'Viva+ Mensal 1000MB - 490$' },
      { value: '850', label: 'Viva+ Mensal 3000MB - 850$' },
      { value: '1650', label: 'Viva+ Mensal 6000MB - 1.650$' },
      { value: '2500', label: 'Viva+ Mensal 10.000MB - 2.500$' },
      { value: '2499', label: '4G 10.000MB - 2.499$' },
      { value: '3500', label: '4G 20.000MB - 3.500$' },
      { value: '4299', label: '4G 50.000MB - 4.299$' },
    ],
  },
  {
    idTemp: 'B7F1CEB9-ADA0-2EA0-C102-F31DAC07C67D', // Alou Pacotes Powa Swag (SISP 144, txCode 3) — BT202601A
    title: 'Escolhe Pacote Powa Swag',
    options: [
      { value: '150', label: 'Semanal 1.000MB - 150$' },
      { value: '250', label: 'Vitamina 2.000MB - 250$' },
      { value: '500', label: 'Mensal 5.000MB - 500$' },
      { value: '1000', label: 'Mega 7.000MB - 1.000$' },
    ],
  },
  {
    idTemp: '64497359-BBCB-5215-6939-B9DBFE1C4F4E', // Alou Pacotes D'kel Bom (SISP 143, txCode 3) — BT202601A
    title: 'Escolhe Pacote D\'Kel Bom',
    options: [
      { value: '300', label: 'Semana 2.000MB - 300$' },
      { value: '700', label: 'Mês 1 5.000MB - 700$' },
      { value: '1000', label: 'Mês 2 7.000MB - 1.000$' },
      { value: '1500', label: 'Mês 3 9.000MB - 1.500$' },
      { value: '3000', label: 'Mês 4 12.000MB - 3.000$' },
      { value: '5000', label: 'Mês 5 15.000MB - 5.000$' },
    ],
  },
  {
    idTemp: 'D3C40965-C2DF-0D34-2CA0-902F264AB0C0', // Unitel Dmeu (SISP 110)
    title: 'Escolhe Pacote D\'Meu',
    options: [
      { value: '300', label: 'D\'Meu Semanal 1.200MB - 300$' },
      { value: '1000', label: 'D\'Meu Mensal 5.000MB - 1.000$' },
      { value: '1500', label: 'D\'Meu Mensal 7.000MB - 1.500$' },
      { value: '3000', label: 'D\'Meu Mensal 12.000MB - 3.000$' },
      { value: '5000', label: 'D\'Meu Mensal 15.000MB - 5.000$' },
    ],
  },
];

// Services that require counter reference lookup
export const COUNTER_REFERENCE_SERVICES = [
  '02F627EB-CDF5-3707-D046-75ED1BDACBEE',
  '268CC5E9-835F-E23E-365F-738E8B77AF1F',
];

// Favorite Service
export interface FavoriteService {
  id: string;
  serviceId: string;
  serviceName: string;
  serviceLogo?: string;
  serviceMimeType?: string;
  categoryId: string;
  categoryName: string;
  addedAt: string;
}

// Saved Reference - for frequently used payment references
export interface SavedReference {
  id: string;
  serviceId: string;
  serviceName: string;
  serviceLogo?: string;
  serviceMimeType?: string;
  label: string; // User-defined label (e.g., "Casa", "Escritório")
  referencia: string;
  entidade?: string;
  tipoServico: PaymentServiceType;
  createdAt: string;
  lastUsed?: string;
}

// Recent Payment - for quick repeat payments
export interface RecentPayment {
  id: string;
  serviceId: string;
  serviceName: string;
  serviceLogo?: string;
  serviceMimeType?: string;
  referencia: string;
  referenciaServico?: string; // nº do contador/telemóvel (para repetir recargas/serviços)
  entidade?: string;
  /** Código SISP da entidade (ex.: '136') — é isto que se envia ao repetir, não o nome */
  entidadeSisp?: string;
  montante: number;
  tipoServico: PaymentServiceType;
  paidAt: string;
  status: 'success' | 'failed' | 'pending';
}

// QR Code Payment Data
export interface QRPaymentData {
  entidade?: string;
  referencia: string;
  montante?: number;
  serviceId?: string;
  tipoServico?: PaymentServiceType;
}
